/**
 * orbitCalculations.ts — Keplerian orbit math + CZML builder.
 *
 * Ported from orbit-1p5-czml.js (Prashant + M365 Copilot).
 *
 * Takes orbital parameters (a, e, i, Ω, ω, ν, epoch) and produces:
 *   1. ECI orbit positions (for Cesium Cartesian3 or CZML)
 *   2. Full CZML packets (time-dynamic satellite + 1.5-orbit polyline)
 *
 * Designed to work with the existing OrbitParameters type:
 *   { semiMajorAxis, eccentricity, inclination, RAAN, AOP, TA, epoch }
 */

import * as Cesium from 'cesium';
import type { MutableRefObject } from 'react';
import { getColourArray } from '../../../utils/commonFunctions';
import { SatelliteGroupDataType } from '../types';

// ═══════════════════════════════════════════════════════════════════════
// Constants
// ═══════════════════════════════════════════════════════════════════════
const DEG2RAD = Math.PI / 180;
const RAD2DEG = 180 / Math.PI;
const MU_EARTH = 398600.4418; // km³/s²
type OrbitReferenceFrame = 'INERTIAL' | 'FIXED';
const INERTIAL_REFERENCE_FRAME: OrbitReferenceFrame = 'INERTIAL';
const FIXED_REFERENCE_FRAME: OrbitReferenceFrame = 'FIXED';

export { DEG2RAD, RAD2DEG, MU_EARTH };

// ═══════════════════════════════════════════════════════════════════════
// Core math
// ═══════════════════════════════════════════════════════════════════════

/** Mean motion in rad/s from semi-major axis (km). */
export function meanMotionRadPerSec(semiMajorAxis_km: number): number {
    return Math.sqrt(MU_EARTH / Math.pow(semiMajorAxis_km, 3));
}

/** Solve Kepler's equation M = E − e·sin(E) → E (rad) using Halley's method. */
export function solveEccentricAnomaly(
    M: number,
    eccentricity: number,
    tol = 1e-10,
    maxIter = 50,
): number {
    let Mnorm = ((M % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    if (eccentricity < 1e-7) return Mnorm;

    // Initial guess: Mnorm for e < 0.8, Math.PI for high e
    let E = eccentricity < 0.8 ? Mnorm : Math.PI;

    for (let k = 0; k < maxIter; k++) {
        const f = E - eccentricity * Math.sin(E) - Mnorm;
        const fp = 1 - eccentricity * Math.cos(E);
        const fpp = eccentricity * Math.sin(E);
        // Halley's 2nd order method refinement
        const dE = f / (fp - (f * fpp) / (2 * fp));
        // Clamp step to avoid overshoot in extreme eccentricity cases
        const clampedDE = Math.max(-0.5, Math.min(0.5, dE));
        E -= clampedDE;
        if (Math.abs(dE) < tol) break;
    }
    return E;
}

/** Eccentric anomaly → true anomaly (rad). */
export function eccentricToTrueAnomaly(E: number, eccentricity: number): number {
    const cosE = Math.cos(E),
        sinE = Math.sin(E);
    const sqrt1me2 = Math.sqrt(1 - eccentricity * eccentricity);
    const cosNu = (cosE - eccentricity) / (1 - eccentricity * cosE);
    const sinNu = (sqrt1me2 * sinE) / (1 - eccentricity * cosE);
    return Math.atan2(sinNu, cosNu);
}

function normalizeRadians(angle: number): number {
    let normalized = angle % (2 * Math.PI);
    if (normalized < 0) normalized += 2 * Math.PI;
    return normalized;
}

/** True anomaly (rad) → mean anomaly (rad), normalized to [0, 2π). */
export function trueToMeanAnomaly(trueAnomaly_rad: number, eccentricity: number): number {
    const nu = normalizeRadians(trueAnomaly_rad);
    if (eccentricity === 0) return nu;

    const eccentricAnomaly = 2 * Math.atan2(
        Math.sqrt(1 - eccentricity) * Math.sin(nu / 2),
        Math.sqrt(1 + eccentricity) * Math.cos(nu / 2),
    );
    return normalizeRadians(eccentricAnomaly - eccentricity * Math.sin(eccentricAnomaly));
}

/** Keplerian → ECI position {x,y,z} in metres (for Cesium interop). */
export function keplerianToECI(
    semiMajorAxis_km: number,
    eccentricity: number,
    inclination_rad: number,
    raan_rad: number,
    aop_rad: number,
    trueAnomaly_rad: number,
): { x: number; y: number; z: number } {
    const p = semiMajorAxis_km * (1 - eccentricity * eccentricity);
    const r = p / (1 + eccentricity * Math.cos(trueAnomaly_rad));

    const x_pqw = r * Math.cos(trueAnomaly_rad);
    const y_pqw = r * Math.sin(trueAnomaly_rad);

    const cosO = Math.cos(raan_rad), sinO = Math.sin(raan_rad);
    const cosI = Math.cos(inclination_rad), sinI = Math.sin(inclination_rad);
    const cosW = Math.cos(aop_rad), sinW = Math.sin(aop_rad);

    // Rotation PQW → ECI
    const r11 = cosO * cosW - sinO * sinW * cosI;
    const r21 = sinO * cosW + cosO * sinW * cosI;
    const r31 = sinW * sinI;

    const r12 = -cosO * sinW - sinO * cosW * cosI;
    const r22 = -sinO * sinW + cosO * cosW * cosI;
    const r32 = cosW * sinI;

    const x_eci = r11 * x_pqw + r12 * y_pqw;
    const y_eci = r21 * x_pqw + r22 * y_pqw;
    const z_eci = r31 * x_pqw + r32 * y_pqw;

    return { x: x_eci * 1000, y: y_eci * 1000, z: z_eci * 1000 };
}

// ═══════════════════════════════════════════════════════════════════════
// Epoch helpers
// ═══════════════════════════════════════════════════════════════════════

/** Compute true anomaly (rad, [0, 2π)) at a given time from epoch + M₀. */
export function trueAnomalyFromEpoch(
    semiMajorAxis_km: number,
    eccentricity: number,
    meanAnomalyAtEpoch_deg: number,
    epochTime: Date,
    currentTime: Date,
): number {
    const n = meanMotionRadPerSec(semiMajorAxis_km);
    const dt = (currentTime.getTime() - epochTime.getTime()) / 1000;
    const M0 = (meanAnomalyAtEpoch_deg || 0) * DEG2RAD;

    let M = M0 + n * dt;
    M = M % (2 * Math.PI);
    if (M < 0) M += 2 * Math.PI;

    const E = solveEccentricAnomaly(M, eccentricity);
    const nu = eccentricToTrueAnomaly(E, eccentricity);
    return normalizeRadians(nu);
}

// ═══════════════════════════════════════════════════════════════════════
// 1.5-orbit point generator
// ═══════════════════════════════════════════════════════════════════════

export interface OrbitGeneratorParams {
    semiMajorAxis_km: number;
    eccentricity: number;
    inclination_deg: number;
    rightAscensionAscendingNode_deg: number;
    argumentOfPerigee_deg: number;
    numPoints?: number;
    orbitTurns?: number;
    /** Direct true anomaly at epoch (degrees). */
    trueAnomalyAtEpoch_deg?: number | null;
    /** Mean anomaly at epoch (degrees) — used with epochTime + currentTime. */
    meanAnomalyAtEpoch_deg?: number | null;
    epochTime?: Date | null;
    currentTime?: Date | null;
    includeEndpoint?: boolean;
}

/**
 * Generate orbit points starting at the satellite's current position
 * and sweeping `orbitTurns` revolutions (default 1.5, non-closing).
 *
 * Returns array of {x, y, z} in metres (ECI frame).
 */
export function generateOrbitPointsWithEpoch(params: OrbitGeneratorParams): { x: number; y: number; z: number }[] {
    const {
        semiMajorAxis_km,
        eccentricity,
        inclination_deg,
        rightAscensionAscendingNode_deg,
        argumentOfPerigee_deg,
        numPoints = 720,
        orbitTurns = 1.5,
        trueAnomalyAtEpoch_deg = null,
        meanAnomalyAtEpoch_deg = null,
        epochTime = null,
        currentTime = null,
        includeEndpoint = true,
    } = params;

    if (semiMajorAxis_km <= 0) throw new Error('semiMajorAxis_km must be > 0');
    if (eccentricity < 0 || eccentricity >= 1) throw new Error('eccentricity must satisfy 0 ≤ e < 1');
    if (orbitTurns <= 0) throw new Error('orbitTurns must be > 0');

    const inclination_rad = inclination_deg * DEG2RAD;
    const raan_rad = rightAscensionAscendingNode_deg * DEG2RAD;
    const aop_rad = argumentOfPerigee_deg * DEG2RAD;

    // Determine starting true anomaly ν₀
    let nu0: number;
    if (trueAnomalyAtEpoch_deg != null) {
        const trueAnomalyAtEpoch_rad = normalizeRadians(trueAnomalyAtEpoch_deg * DEG2RAD);
        if (
            epochTime instanceof Date &&
            currentTime instanceof Date &&
            epochTime.getTime() !== currentTime.getTime()
        ) {
            const meanAnomalyAtEpoch_deg = trueToMeanAnomaly(trueAnomalyAtEpoch_rad, eccentricity) * RAD2DEG;
            nu0 = trueAnomalyFromEpoch(semiMajorAxis_km, eccentricity, meanAnomalyAtEpoch_deg, epochTime, currentTime);
        } else {
            nu0 = trueAnomalyAtEpoch_rad;
        }
    } else if (meanAnomalyAtEpoch_deg != null && epochTime instanceof Date && currentTime instanceof Date) {
        nu0 = trueAnomalyFromEpoch(semiMajorAxis_km, eccentricity, meanAnomalyAtEpoch_deg, epochTime, currentTime);
    } else {
        nu0 = 0;
    }

    const totalSweep = 2 * Math.PI * orbitTurns;
    const samples = Math.max(2, Math.round(numPoints * orbitTurns));
    const positions: { x: number; y: number; z: number }[] = [];

    for (let k = 0; k < samples; k++) {
        const frac = includeEndpoint ? k / (samples - 1) : k / samples;
        const nu = nu0 + frac * totalSweep;
        positions.push(keplerianToECI(semiMajorAxis_km, eccentricity, inclination_rad, raan_rad, aop_rad, nu));
    }

    return positions;
}

// ═══════════════════════════════════════════════════════════════════════
// CZML Builder (single satellite)
// ═══════════════════════════════════════════════════════════════════════

export interface BuildOrbitCzmlOptions {
    semiMajorAxis_km: number;
    eccentricity: number;
    inclination_deg: number;
    rightAscensionAscendingNode_deg: number;
    argumentOfPerigee_deg: number;
    numPoints?: number;
    orbitTurns?: number;
    trueAnomalyAtEpoch_deg?: number | null;
    meanAnomalyAtEpoch_deg?: number | null;
    epochTime: Date;
    currentTime?: Date | null;
    entityId?: string;
    name?: string;
    colorRgba?: number[];
    showStaticOrbitPolyline?: boolean;
    includeEndpoint?: boolean;
    referenceFrame?: OrbitReferenceFrame;
    convertInertialToFixed?: boolean;
}

function getInertialToFixedMatrix(time: Cesium.JulianDate): Cesium.Matrix3 {
    return (
        Cesium.Transforms.computeIcrfToFixedMatrix(time) ||
        Cesium.Transforms.computeTemeToPseudoFixedMatrix(time)
    );
}

function convertEciToFixed(position: { x: number; y: number; z: number }, time: Cesium.JulianDate): { x: number; y: number; z: number } {
    const matrix = getInertialToFixedMatrix(time);
    const inertial = new Cesium.Cartesian3(position.x, position.y, position.z);
    const fixed = Cesium.Matrix3.multiplyByVector(matrix, inertial, new Cesium.Cartesian3());
    return {
        x: fixed.x,
        y: fixed.y,
        z: fixed.z,
    };
}

/**
 * Build a CZML packet array for a single time-dynamic satellite path
 * over `orbitTurns` revolutions (default 1.5) in the INERTIAL frame,
 * plus an optional static polyline track.
 *
 * Returns an array of CZML packets (no document packet — caller adds it).
 */
export function buildSatelliteCzmlPackets(options: BuildOrbitCzmlOptions): any[] {
    const {
        semiMajorAxis_km,
        eccentricity,
        inclination_deg,
        rightAscensionAscendingNode_deg,
        argumentOfPerigee_deg,
        numPoints = 720,
        orbitTurns = 1.5,
        trueAnomalyAtEpoch_deg = null,
        meanAnomalyAtEpoch_deg = null,
        epochTime,
        currentTime: currentTimeOpt = null,
        entityId = 'satellite-1',
        name = 'Satellite',
        colorRgba = [230, 166, 78, 255],
        showStaticOrbitPolyline = true,
        includeEndpoint = true,
        referenceFrame = INERTIAL_REFERENCE_FRAME,
        convertInertialToFixed = false,
    } = options;

    const start = currentTimeOpt instanceof Date ? currentTimeOpt : epochTime;

    const positionsECI = generateOrbitPointsWithEpoch({
        semiMajorAxis_km,
        eccentricity,
        inclination_deg,
        rightAscensionAscendingNode_deg,
        argumentOfPerigee_deg,
        numPoints,
        orbitTurns,
        trueAnomalyAtEpoch_deg,
        meanAnomalyAtEpoch_deg,
        epochTime,
        currentTime: start,
        includeEndpoint,
    });

    const n = meanMotionRadPerSec(semiMajorAxis_km);
    const periodSec = (2 * Math.PI) / n;
    const samples = positionsECI.length;
    const totalSpanSec = periodSec * orbitTurns;
    const sampledSpanSec = includeEndpoint ? totalSpanSec : totalSpanSec * ((samples - 1) / samples);
    const end = new Date(start.getTime() + sampledSpanSec * 1000);

    // Sampled position: [elapsed_sec, x, y, z, ...]
    const dt = sampledSpanSec / (samples - 1);
    const cartesianSamples: number[] = [];
    const displayPositions: { x: number; y: number; z: number }[] = [];
    const startJulianDate = Cesium.JulianDate.fromDate(start);
    for (let k = 0; k < samples; k++) {
        const t = k * dt;
        const sampleTime = Cesium.JulianDate.addSeconds(startJulianDate, t, new Cesium.JulianDate());
        const p = convertInertialToFixed ? convertEciToFixed(positionsECI[k], sampleTime) : positionsECI[k];
        displayPositions.push(p);
        cartesianSamples.push(t, p.x, p.y, p.z);
    }

    const availability = `${start.toISOString()}/${end.toISOString()}`;
    const packets: any[] = [];

    // Satellite packet
    packets.push({
        id: entityId,
        name,
        availability,
        position: {
            referenceFrame,
            epoch: start.toISOString(),
            cartesian: cartesianSamples,
        },
        point: {
            pixelSize: 10,
            color: { rgba: colorRgba },
            outlineColor: { rgba: [255, 255, 255, 255] },
            outlineWidth: 2,
        },
        billboard: {
            eyeOffset: { cartesian: [0, 0, 0] },
            horizontalOrigin: 'CENTER',
            image: '/assets/satellite_inactive.svg',
            pixelOffset: { cartesian2: [0, 0] },
            scale: 1,
            show: true,
            verticalOrigin: 'CENTER',
        },
        label: {
            text: name,
            font: 'montserrat',
            fillColor: { rgba: [255, 255, 255, 255] },
            outlineColor: { rgba: [0, 0, 0, 255] },
            outlineWidth: 2,
            pixelOffset: { cartesian2: [10, 0] },
            showBackground: false,
            show: false,
            style: 'FILL_AND_OUTLINE',
            horizontalOrigin: 'LEFT',
            verticalOrigin: 'CENTER',
        },
        path: {
            show: true,
            width: 2,
            material: { polylineGlow: { glowPower: 0.15, color: { rgba: colorRgba } } },
            leadTime: sampledSpanSec,
            trailTime: sampledSpanSec,
            resolution: 60,
        },
    });

    // Static polyline track
    if (showStaticOrbitPolyline) {
        const flatPositions: number[] = [];
        for (const p of displayPositions) {
            flatPositions.push(p.x, p.y, p.z);
        }
        packets.push({
            id: `${entityId}-track`,
            name: `${name} Orbit Track`,
            polyline: {
                positions: {
                    referenceFrame,
                    cartesian: flatPositions,
                },
                width: 1,
                material: { solidColor: { color: { rgba: colorRgba } } },
                arcType: 'NONE',
            },
        });
    }

    return packets;
}

// ═══════════════════════════════════════════════════════════════════════
// Full CZML (with document packet)
// ═══════════════════════════════════════════════════════════════════════

/**
 * Build a complete CZML array (document + satellite + optional track)
 * for a single satellite — ready to feed into `CzmlDataSource.load()`.
 */
export function buildOrbitCzml(options: BuildOrbitCzmlOptions): any[] {
    const start = options.currentTime instanceof Date ? options.currentTime : options.epochTime;
    const n = meanMotionRadPerSec(options.semiMajorAxis_km);
    const periodSec = (2 * Math.PI) / n;
    const totalSpanSec = periodSec * (options.orbitTurns ?? 1.5);
    const end = new Date(start.getTime() + totalSpanSec * 1000);

    const docPacket = {
        id: 'document',
        version: '1.0',
        name: options.name || 'Satellite Orbit',
        clock: {
            interval: `${start.toISOString()}/${end.toISOString()}`,
            currentTime: start.toISOString(),
            multiplier: 1,
            range: 'LOOP_STOP',
            step: 'SYSTEM_CLOCK_MULTIPLIER',
        },
    };

    return [docPacket, ...buildSatelliteCzmlPackets(options)];
}

// ═══════════════════════════════════════════════════════════════════════
// High-level: Group-aware orbit CZML builder
//
// This is the drop-in replacement for createOrbitsPackets().
// Instead of orbit_points from the API, it uses each satellite's
// orbitParameters to compute the orbit client-side.
// ═══════════════════════════════════════════════════════════════════════

const MODELLING_DEFAULT_ORBIT_COLOR = '[67, 87, 196, 1]';

/**
 * Compute and plot orbits for all satellite groups using their
 * orbital parameters (no orbit API needed).
 *
 * Creates one `CzmlDataSource` per group (named by `groupId`),
 * matching the existing pattern so that `highlightGroup()`,
 * `toggleHideShowEntity()` etc. continue to work.
 *
 * @param viewer         - Cesium Viewer
 * @param groups         - Satellite group data (from getAllSatelliteGroups API)
 * @param hiddenOrbits   - Group IDs that should start hidden
 * @param orbitTurns     - Number of orbit revolutions to draw (default 1.5)
 * @param numPoints      - Points per orbit revolution (default 720)
 */
export function createOrbitsFromParams(
    viewer: Cesium.Viewer | null,
    groups: SatelliteGroupDataType[],
    hiddenOrbits: string[],
    orbitTurns = 1.5,
    numPoints = 720,
): void {
    if (!viewer) return;

    try {
        const now = new Date();

        groups.forEach((group) => {
            const groupId = group.groupId;
            const show = !hiddenOrbits.includes(groupId);
            const packets: any[] = [];

            // Determine time span across all satellites in this group
            let minEpoch = Infinity;
            let maxEndMs = -Infinity;

            // Pre-compute spans
            const satSpans: { start: Date; end: Date; sat: typeof group.satellites[0] }[] = [];
            for (const sat of group.satellites) {
                const op = sat.orbitParameters;
                if (!op || !op.semiMajorAxis || op.semiMajorAxis <= 0) continue;

                const epochMs = op.epoch ? op.epoch * 1000 : now.getTime();
                const epochDate = new Date(epochMs);

                const n = meanMotionRadPerSec(op.semiMajorAxis);
                const periodSec = (2 * Math.PI) / n;
                const totalSpanSec = periodSec * orbitTurns;
                const endDate = new Date(epochDate.getTime() + totalSpanSec * 1000);

                if (epochDate.getTime() < minEpoch) minEpoch = epochDate.getTime();
                if (endDate.getTime() > maxEndMs) maxEndMs = endDate.getTime();

                satSpans.push({ start: epochDate, end: endDate, sat });
            }

            if (satSpans.length === 0) return;

            // Use the widest time window across all sats in the group
            const globalStart = new Date(minEpoch);
            const globalEnd = new Date(maxEndMs);
            const availability = `${globalStart.toISOString()}/${globalEnd.toISOString()}`;

            for (const { start, sat } of satSpans) {
                const op = sat.orbitParameters;
                const orbitColorStr = (sat.orbitColour && !sat.orbitColour?.includes('#'))
                    ? sat.orbitColour
                    : MODELLING_DEFAULT_ORBIT_COLOR;
                const colorArray = getColourArray(orbitColorStr);
                const colorRgba = [colorArray[0], colorArray[1], colorArray[2], 255];

                const satPackets = buildSatelliteCzmlPackets({
                    semiMajorAxis_km: op.semiMajorAxis,
                    eccentricity: op.eccentricity,
                    inclination_deg: op.inclination,
                    rightAscensionAscendingNode_deg: op.RAAN,
                    argumentOfPerigee_deg: op.AOP,
                    trueAnomalyAtEpoch_deg: op.TA,
                    epochTime: start,
                    currentTime: start,
                    numPoints,
                    orbitTurns,
                    entityId: sat.id,
                    name: sat.name,
                    colorRgba,
                    showStaticOrbitPolyline: true,
                });

                // Patch: override availability and add parent groupId
                for (const pkt of satPackets) {
                    pkt.availability = availability;
                    pkt.show = show;
                    if (!pkt.polyline) {
                        pkt.parent = groupId;
                        pkt.description = 'orbit';
                    }
                }

                packets.push(...satPackets);
            }

            // Remove old data source for this group, then add new one
            removeDataSourceByName(viewer, groupId);

            const dataSource = new Cesium.CzmlDataSource();
            viewer.dataSources.add(
                dataSource.load([
                    {
                        id: 'document',
                        name: groupId,
                        version: '1.0',
                        clock: {
                            interval: availability,
                            currentTime: availability.split('/')[0],
                            multiplier: 0,
                            range: 'CLAMPED',
                            step: 'SYSTEM_CLOCK_MULTIPLIER',
                        },
                    },
                    ...packets,
                ]),
            );
        });

        requestRenderViewer(viewer);
    } catch (error) {
        console.error('[createOrbitsFromParams] Error:', error);
    }
}

// ── Helpers copied from visualization.ts to keep this file standalone ──

function removeDataSourceByName(viewer: Cesium.Viewer, name: string): void {
    const ds = viewer.dataSources;
    for (let i = 0; i < ds.length; i++) {
        const d = ds.get(i);
        if (d.name === name) {
            ds.remove(d);
            return;
        }
    }
}

function requestRenderViewer(viewer: Cesium.Viewer): void {
    viewer.scene.requestRender();
    setTimeout(() => viewer.scene.requestRender(), 100);
}

// ═══════════════════════════════════════════════════════════════════════
// Shared orbit-preview utilities
//
// Used by both Mission Modelling (useOrbitPreview) and
// Scenario creation (SatelliteSelectionModal) flows.
// ═══════════════════════════════════════════════════════════════════════

const EARTH_RADIUS_KM = 6378.137; 
const PREVIEW_NUM_POINTS = 360;
const PREVIEW_ORBIT_TURNS = 1;

/** Satellite definition used when building preview CZML. */
export interface PreviewSatDef {
    id: string;
    name: string;
    raan: number;
    ta: number;
    plane: number;
}

/**
 * Parse a stored color string (e.g. `"[67, 87, 196, 1]"`) into RGBA.
 * Falls back to `fallback` (default teal).
 */
export function parseGroupColorRgba(
    color: string | undefined | null,
    fallback: [number, number, number, number] = [0, 230, 180, 220],
): [number, number, number, number] {
    try {
        if (color && !color.includes('#')) {
            const arr = JSON.parse(color);
            if (Array.isArray(arr) && arr.length >= 3) {
                return [
                    Math.round(arr[0]),
                    Math.round(arr[1]),
                    Math.round(arr[2]),
                    arr.length >= 4 ? Math.round(arr[3] <= 1 ? arr[3] * 255 : arr[3]) : 255,
                ];
            }
        }
    } catch { /* ignore */ }
    return [...fallback];
}

/**
 * Generate distinct HSL-based colors for each orbital plane.
 * If `totalPlanes ≤ 1`, returns `[baseColor]`.
 */
export function generatePlaneColors(
    baseColor: [number, number, number, number],
    totalPlanes: number,
): [number, number, number, number][] {
    if (totalPlanes <= 1) return [[...baseColor]];

    const out: [number, number, number, number][] = [];
    for (let p = 0; p < totalPlanes; p++) {
        const hue = (p / totalPlanes) * 360;
        const s = 0.85, l = 0.6;
        const c = (1 - Math.abs(2 * l - 1)) * s;
        const x = c * (1 - Math.abs(((hue / 60) % 2) - 1));
        const m = l - c / 2;
        let r = 0, g = 0, b = 0;
        if (hue < 60)       { r = c; g = x; }
        else if (hue < 120) { r = x; g = c; }
        else if (hue < 180) { g = c; b = x; }
        else if (hue < 240) { g = x; b = c; }
        else if (hue < 300) { r = x; b = c; }
        else                { r = c; b = x; }
        out.push([
            Math.round((r + m) * 255),
            Math.round((g + m) * 255),
            Math.round((b + m) * 255),
            220,
        ]);
    }
    return out;
}

/** Options for building a group's orbit preview CZML. */
export interface GroupPreviewParams {
    groupId: string;
    groupName: string;
    altitude: number;
    eccentricity: number;
    inclination: number;
    RAAN: number;
    AP: number;
    TA: number;
    /** epoch in seconds or milliseconds */
    epoch: number;
    /** preview/scenario start time in seconds or milliseconds. Defaults to epoch. */
    startTime?: number;
    satCount: number;
    noOfPlanes: number;
    noOfSatellitesPerPlane: number;
    phasingAngle: number;
    /** RAAN separation between planes (degrees). Defaults to 360/noOfPlanes if not set. */
    planeSeparationAngle?: number;
    isMultiPlane: boolean;
    color: string;
}

/**
 * Build CZML packets for a single group's orbit preview.
 *
 * Returns `{ packets, startMs, endMs }` — caller assembles the document
 * packet and handles loading into the viewer.
 */
export function buildGroupPreviewPackets(params: GroupPreviewParams): {
    packets: any[];
    startMs: number;
    endMs: number;
} {
    const {
        groupId, groupName,
        altitude, eccentricity, inclination,
        RAAN, AP, TA, epoch, startTime,
        satCount, noOfPlanes, noOfSatellitesPerPlane,
        phasingAngle, planeSeparationAngle, isMultiPlane, color,
    } = params;

    const perigeeRadius = altitude + EARTH_RADIUS_KM;
    const sma = eccentricity > 0 ? perigeeRadius / (1 - eccentricity) : perigeeRadius;
    const toEpochMilliseconds = (value: number | undefined): number | null => {
        if (value === undefined || !Number.isFinite(value)) return null;
        return value > 1e12 ? value : value * 1000;
    };

    const epochMs = toEpochMilliseconds(epoch) ?? Date.now();
    const previewStartMs = toEpochMilliseconds(startTime) ?? epochMs;
    const epochDate = new Date(epochMs);
    const previewStartDate = new Date(previewStartMs);
    const baseColor = parseGroupColorRgba(color);

    // Build satellite definitions
    const sats: PreviewSatDef[] = [];
    let totalPlanes = 1;

    if (isMultiPlane && noOfPlanes > 1) {
        totalPlanes = noOfPlanes;
        const spp = noOfSatellitesPerPlane || 1;
        const raanSep = (planeSeparationAngle != null && planeSeparationAngle > 0)
            ? planeSeparationAngle
            : 360 / noOfPlanes;
        const taPhasing = (phasingAngle != null && phasingAngle > 0)
            ? phasingAngle
            : (spp > 1 ? 360 / spp : 0);
        let idx = 0;
        for (let p = 0; p < totalPlanes; p++) {
            const planeRAAN = (RAAN + p * raanSep) % 360;
            for (let s = 0; s < spp; s++) {
                sats.push({
                    id: `${groupId}-sat-${idx}`,
                    name: `P${p + 1}-S${s + 1}`,
                    raan: planeRAAN,
                    ta: (TA + s * taPhasing) % 360,
                    plane: p,
                });
                idx++;
            }
        }
    } else {
        totalPlanes = 1;
        const count = satCount || 1;
        const phasing = phasingAngle !== undefined && phasingAngle !== null ? phasingAngle : (count > 1 ? 360 / count : 0);
        for (let i = 0; i < count; i++) {
            sats.push({
                id: `${groupId}-sat-${i}`,
                name: count === 1 ? groupName : `Sat ${i + 1}`,
                raan: RAAN,
                ta: (TA + i * phasing) % 360,
                plane: 0,
            });
        }
    }

    // Time span
    const n = meanMotionRadPerSec(sma);
    const periodSec = (2 * Math.PI) / n;
    const previewSpanSec = periodSec * PREVIEW_ORBIT_TURNS * ((PREVIEW_NUM_POINTS - 1) / PREVIEW_NUM_POINTS);
    const endMs = previewStartDate.getTime() + previewSpanSec * 1000;

    // Per-plane colors
    const pColors = generatePlaneColors(baseColor, totalPlanes);

    // CZML packets
    const packets: any[] = [];
    for (const sat of sats) {
        const col = pColors[sat.plane] || baseColor;
        packets.push(
            ...buildSatelliteCzmlPackets({
                semiMajorAxis_km: sma,
                eccentricity,
                inclination_deg: inclination,
                rightAscensionAscendingNode_deg: sat.raan,
                argumentOfPerigee_deg: AP,
                trueAnomalyAtEpoch_deg: sat.ta,
                epochTime: epochDate,
                currentTime: previewStartDate,
                numPoints: PREVIEW_NUM_POINTS,
                orbitTurns: PREVIEW_ORBIT_TURNS,
                entityId: sat.id,
                name: sat.name,
                colorRgba: col,
                showStaticOrbitPolyline: false,
                includeEndpoint: false,
                referenceFrame: FIXED_REFERENCE_FRAME,
                convertInertialToFixed: true,
            }),
        );
    }

    return { packets, startMs: previewStartDate.getTime(), endMs };
}

/**
 * Build a complete CZML array (document + all group packets) for multiple
 * groups, ready to be loaded into a `CzmlDataSource`.
 */
export function buildMultiGroupPreviewCzml(
    groups: GroupPreviewParams[],
    dataSourceName: string,
): any[] | null {
    let globalMinEpoch = Infinity;
    let globalMaxEnd = -Infinity;
    const allPackets: any[] = [];

    for (const gp of groups) {
        if (
            !isFinite(gp.altitude) || gp.altitude <= 0 ||
            !isFinite(gp.eccentricity) || gp.eccentricity < 0 || gp.eccentricity >= 1 ||
            !isFinite(gp.inclination) || !isFinite(gp.RAAN) ||
            !isFinite(gp.AP) || !isFinite(gp.TA)
        ) {
            continue;
        }

        const { packets, startMs, endMs } = buildGroupPreviewPackets(gp);
        allPackets.push(...packets);
        if (startMs < globalMinEpoch) globalMinEpoch = startMs;
        if (endMs > globalMaxEnd) globalMaxEnd = endMs;
    }

    if (allPackets.length === 0) return null;

    const gStart = new Date(globalMinEpoch);
    const gEnd = new Date(globalMaxEnd);
    const availability = `${gStart.toISOString()}/${gEnd.toISOString()}`;

    for (const pkt of allPackets) {
        pkt.availability = availability;
    }

    return [
        {
            id: 'document',
            version: '1.0',
            name: dataSourceName,
            clock: {
                interval: availability,
                currentTime: gStart.toISOString(),
                multiplier: 0,
                range: 'CLAMPED',
                step: 'SYSTEM_CLOCK_MULTIPLIER',
            },
        },
        ...allPackets,
    ];
}

function syncViewerClockToPreviewCzml(viewer: Cesium.Viewer, czml: any[]): void {
    const documentPacket = czml.find((packet) => packet?.id === 'document');
    const clock = documentPacket?.clock;
    if (!clock?.interval) return;

    const [startIso, endIso] = String(clock.interval).split('/');
    const currentIso = clock.currentTime || startIso;
    if (!startIso || !endIso || !currentIso) return;

    const start = Cesium.JulianDate.fromIso8601(startIso);
    const stop = Cesium.JulianDate.fromIso8601(endIso);
    const current = Cesium.JulianDate.fromIso8601(currentIso);

    viewer.clock.startTime = Cesium.JulianDate.clone(start);
    viewer.clock.stopTime = Cesium.JulianDate.clone(stop);
    viewer.clock.currentTime = Cesium.JulianDate.clone(current);
    viewer.clock.multiplier = clock.multiplier ?? 0;
    viewer.clock.clockRange = Cesium.ClockRange.CLAMPED;
    viewer.clock.clockStep = Cesium.ClockStep.SYSTEM_CLOCK_MULTIPLIER;
    viewer.clock.shouldAnimate = false;
}

/**
 * Remove a named CzmlDataSource from a Cesium viewer.
 */
export function removePreviewDataSource(
    viewer: Cesium.Viewer | null,
    dataSourceName: string,
): void {
    if (!viewer) return;
    try {
        for (let i = viewer.dataSources.length - 1; i >= 0; i--) {
            const ds = viewer.dataSources.get(i);
            if (ds.name === dataSourceName) {
                viewer.dataSources.remove(ds, true);
            }
        }
        viewer.scene.requestRender();
    } catch { /* viewer may be destroyed */ }
}

/**
 * Convenience wrapper used inside React components.  Many of our
 * orbit-preview consumers hold a `useRef<CzmlDataSource|null>` so we can
 * remove the *same* data-source object from the viewer when updating.
 * This utility combines that pattern with the generic
 * `removePreviewDataSource` behaviour, avoiding duplicated logic across
 * several files.
 */
export function removePreviewWithRef(
    viewer: Cesium.Viewer | null,
    dsRef: MutableRefObject<Cesium.CzmlDataSource | null> | null,
    dataSourceName: string,
): void {
    if (dsRef && dsRef.current && viewer) {
        try { viewer.dataSources.remove(dsRef.current, true); } catch { /* ignore */ }
        dsRef.current = null;
    }
    removePreviewDataSource(viewer, dataSourceName);
}

/**
 * Load a preview CZML into the viewer, replacing any existing one
 * with the same data-source name.
 *
 * Returns the new CzmlDataSource (or null if nothing to load).
 */
export function loadPreviewDataSource(
    viewer: Cesium.Viewer | null,
    czml: any[] | null,
    dataSourceName: string,
): Cesium.CzmlDataSource | null {
    if (!viewer || !czml) return null;

    removePreviewDataSource(viewer, dataSourceName);

    const ds = new Cesium.CzmlDataSource(dataSourceName);
    viewer.dataSources
        .add(ds.load(czml))
        .then(() => {
            syncViewerClockToPreviewCzml(viewer, czml);
            viewer.scene.requestRender();
        })
        .catch((err: any) => console.warn(`[${dataSourceName}] CZML load error:`, err));

    return ds;
}
